<template>
  <div class="dropdown_container" :class="[{ dropdown_disabled: disabled }, width || 'w-fit']">
    <div class="hover_area" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
      <div v-if="show" :class="['trigger_container', width]" @click="handleTrigger">
        <slot name="trigger"></slot>
      </div>
      <div
        v-if="isOpen"
        ref="dropdownRef"
        :class="[
          'dropdown',
          {
            fade_out: isClosing,
            center: position === 'center',
            top: position === 'top',
            topstart: position === 'topstart',
            topend: position === 'topend',
            left: position === 'left',
            right: position === 'right',
          },
          dropdownCSS,
        ]"
        :style="{
          ...dropdownStyle,
          '--dropdown-position': relativeToViewport ? 'fixed' : 'absolute',
        }"
        @mouseenter="clearCloseTimeout"
        @mouseleave="handleMouseLeave"
      >
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick } from 'vue';

const props = defineProps({
  onHover: { type: Boolean, default: false },
  maxHeight: { type: String, default: null },
  gap: { type: String, default: null },
  position: {
    type: String,
    default: 'start',
    validator: (value) => ['start', 'end', 'center', 'top', 'topstart', 'topend', 'left', 'right'].includes(value),
  },
  show: { type: Boolean, default: true },
  dropdownCSS: { type: String, default: 'p-2' },
  relativeToViewport: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  overflowY: {
    type: String,
    default: 'scroll',
    validator: (value) => ['scroll', 'auto', 'hidden', 'visible'].includes(value),
  },
  width: { type: String, default: '' },
  hoverDelay: { type: Number, default: 1000 },
});

const emit = defineEmits(['close', 'triggerClick']);

const isOpen = ref(false);
const isClosing = ref(false);
const dropdownRef = ref(null);
const closeTimeoutId = ref(null);

const handleClickOutside = (event) => {
  if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
    closeDropdown();
  }
};

const clearCloseTimeout = () => {
  if (closeTimeoutId.value) {
    clearTimeout(closeTimeoutId.value);
    closeTimeoutId.value = null;
  }
};

const handleTrigger = () => {
  if (props.disabled) {
    return;
  }
  emit('triggerClick', {
    isOpen: isOpen.value,
    event: 'click',
  });
  if (isOpen.value) {
    closeDropdown();
  } else {
    isOpen.value = true;
    nextTick(() => {
      updateDropdownPosition();
    });
  }
};

const handleMouseEnter = () => {
  if (props.onHover && !props.disabled) {
    clearCloseTimeout();
    isOpen.value = true;
  }
};

const handleMouseLeave = () => {
  if (props.onHover) {
    clearCloseTimeout();
    closeTimeoutId.value = setTimeout(() => {
      closeDropdown();
    }, props.hoverDelay);
  }
};

const closeDropdown = () => {
  isClosing.value = true;
  setTimeout(() => {
    isOpen.value = false;
    isClosing.value = false;
    emit('close');
  }, 100);
};

const dropdownPosition = ref({});

const updateDropdownPosition = () => {
  const rect = dropdownRef.value?.getBoundingClientRect();
  const triggerRect = dropdownRef.value?.parentElement?.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;

  if (props.relativeToViewport && rect && triggerRect) {
    const intendedPosition = {
      start: {
        top: `${triggerRect.bottom}px`,
        left: `${triggerRect.left}px`,
      },
      end: {
        top: `${triggerRect.bottom}px`,
        right: `${viewportWidth - triggerRect.right}px`,
        left: 'auto',
      },
      center: {
        top: `${triggerRect.bottom}px`,
        left: `${triggerRect.left + triggerRect.width / 2 - rect.width / 2}px`,
      },
      top: {
        top: `${triggerRect.top - rect.height}px`,
        left: `${triggerRect.left}px`,
      },
      topstart: {
        top: `${triggerRect.top - rect.height}px`,
        left: `${triggerRect.left}px`,
      },
      topend: {
        top: `${triggerRect.top - rect.height}px`,
        left: `${triggerRect.right - rect.width}px`,
      },
      left: {
        top: `${triggerRect.top}px`,
        left: `${triggerRect.left - rect.width}px`,
      },
      right: {
        top: `${triggerRect.top}px`,
        left: `${triggerRect.right}px`,
      },
    }[props.position];

    dropdownPosition.value = intendedPosition;

    if (parseInt(intendedPosition.top) + rect.height > viewportHeight) {
      dropdownPosition.value.top = `${triggerRect.top - rect.height + 50}px`;
    }

    if (props.position === 'end') {
      const rightEdge = viewportWidth - (parseInt(dropdownPosition.value.right) || 0);
      if (rightEdge - rect.width < 0) {
        dropdownPosition.value.right = `${viewportWidth - triggerRect.left}px`;
      }
    } else {
      if (parseInt(intendedPosition.left) + rect.width > viewportWidth) {
        dropdownPosition.value.left = `${triggerRect.right - rect.width}px`;
      } else if (parseInt(intendedPosition.left) < 0) {
        dropdownPosition.value.left = `${triggerRect.left}px`;
      }
    }
  } else {
    dropdownPosition.value = {
      start: { left: '0' },
      end: { right: '0', left: 'auto' },
      center: { left: '50%', transform: 'translateX(-50%)' },
      top: { bottom: '100%', left: '0' },
      topstart: { bottom: '100%', left: '0' },
      topend: { bottom: '100%', right: '0' },
      left: { right: '100%', top: '0' },
      right: { left: '100%', top: '0' },
    }[props.position];
  }
};

const dropdownStyle = computed(() => ({
  maxHeight: props.maxHeight,
  overflowY: props.overflowY,
  ...(props.position.startsWith('top') ? { marginBottom: props.gap } : props.position === 'left' || props.position === 'right' ? { margin: '0 ' + props.gap } : { marginTop: props.gap }),
  ...dropdownPosition.value,
}));

onMounted(() => {
  document.addEventListener('mousedown', handleClickOutside);
  nextTick(() => {
    updateDropdownPosition();
  });
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
  clearCloseTimeout();
});

watch(isOpen, (newValue) => {
  if (newValue) {
    nextTick(() => {
      updateDropdownPosition();
    });
  }
});

defineExpose({
  toggleDropdown: handleTrigger,
  closeDropdown: () => {
    isOpen.value = false;
  },
  isOpen,
});
</script>

<style scoped>
.dropdown_container {
  position: relative;
  display: inline-block;
  height: fit-content;
}

.hover_area {
  padding: 8px;
  margin: -8px;
}

.trigger_container {
  display: inline-block;
}

.dropdown {
  position: var(--dropdown-position, absolute);
  color: #fff;
  border-radius: 8px;
  background: var(--dropdown-bg);
  box-shadow: var(--dropdown-boxshadow);
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
  opacity: 0;
  animation: fade_in 0.3s forwards;
  width: max-content;
  z-index: 10;
}

.dropdown.center {
  left: 50%;
  transform: translateX(-50%);
  animation: fade_in_center 0.3s forwards;
}

.dropdown.top,
.dropdown.topstart,
.dropdown.topend {
  animation: fade_in_top 0.3s forwards;
}

.dropdown.left {
  animation: fade_in_left 0.3s forwards;
}

.dropdown.right {
  animation: fade_in_right 0.3s forwards;
}

.dropdown.center.fade_out {
  animation: fade_out_center 0.3s forwards;
}

.dropdown.top.fade_out,
.dropdown.topstart.fade_out,
.dropdown.topend.fade_out {
  animation: fade_out_top 0.3s forwards;
}

.dropdown.left.fade_out {
  animation: fade_out_left 0.3s forwards;
}

.dropdown.right.fade_out {
  animation: fade_out_right 0.3s forwards;
}

@keyframes fade_in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade_out {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-10px);
  }
}

@keyframes fade_in_center {
  from {
    opacity: 0;
    transform: translateY(-10px) translateX(-50%);
  }
  to {
    opacity: 1;
    transform: translateY(0) translateX(-50%);
  }
}

@keyframes fade_out_center {
  from {
    opacity: 1;
    transform: translateY(0) translateX(-50%);
  }
  to {
    opacity: 0;
    transform: translateY(-10px) translateX(-50%);
  }
}

@keyframes fade_in_top {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fade_out_top {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(10px);
  }
}

@keyframes fade_in_left {
  from {
    opacity: 0;
    transform: translateX(10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fade_out_left {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(10px);
  }
}

@keyframes fade_in_right {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fade_out_right {
  from {
    opacity: 1;
    transform: translateX(0);
  }
  to {
    opacity: 0;
    transform: translateX(-10px);
  }
}

.fade_out:not(.center):not(.top):not(.topstart):not(.topend):not(.left):not(.right) {
  animation: fade_out 0.3s forwards;
}

.dropdown_disabled {
  opacity: 0.5;
  pointer-events: none;
  cursor: not-allowed !important;
}
</style>
